import React, { useEffect, useState } from "react";
import axios from "axios";
import { fmtMoney } from "../components/ops/opsUtils";
import { Sparkles, Building2, FileSpreadsheet, TrendingUp, Receipt } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function DemoAttorneyPortal() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    axios.get(`${API}/demo/attorney/firm`).then((r) => setData(r.data)).finally(() => setLoading(false));
  }, []);
  if (loading) return <Splash label="Loading demo…" />;
  if (!data) return null;
  return (
    <div className="min-h-screen bg-[#F7F9FC]" data-testid="demo-attorney-portal">
      <DemoBanner />
      <div className="max-w-[1300px] mx-auto p-6 md:p-8">
        <div className="mb-6">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-[#6B7280] mb-1">
            <Building2 className="w-3.5 h-3.5" /> Attorney portal · {data.firm.name}
          </div>
          <h1 className="font-display text-4xl font-semibold tracking-tight text-[#0A2540]">Your firm's cases</h1>
          <p className="text-sm text-[#6B7280] mt-1">All cases for {data.firm.name} — shared across attorneys, paralegals, and case managers.</p>
        </div>
        <div className="grid sm:grid-cols-4 gap-4 mb-6">
          <Card label="Active cases" value={data.summary.active_count} accent />
          <Card label="Total cases" value={data.summary.loan_count} />
          <Card label="Outstanding balance" value={fmtMoney(data.summary.total_outstanding_payoff)} />
          <Card label="Total funded" value={fmtMoney(data.summary.total_principal)} />
        </div>
        <div className="bg-white border border-[#E5E7EB] rounded-lg overflow-hidden">
          <div className="px-5 py-3 border-b border-[#E5E7EB] font-medium text-[#0A2540]">Active cases</div>
          <table className="w-full text-sm">
            <thead className="bg-[#F9FAFB]">
              <tr className="text-left text-[11px] uppercase tracking-wider text-[#6B7280]">
                <th className="px-4 py-2.5 font-medium">Client</th>
                <th className="px-4 py-2.5 font-medium">Funded</th>
                <th className="px-4 py-2.5 font-medium">Status</th>
                <th className="px-4 py-2.5 font-medium text-right">Principal</th>
                <th className="px-4 py-2.5 font-medium text-right">Current payoff</th>
              </tr>
            </thead>
            <tbody>
              {data.loans.map((ln) => (
                <tr key={ln.loan_id} className="border-b border-[#F3F4F6] hover:bg-[#F9FAFB]" data-testid={`demo-loan-${ln.loan_id}`}>
                  <td className="px-4 py-3 text-[#0A2540] font-medium">{ln.client_name}</td>
                  <td className="px-4 py-3 text-xs text-[#6B7280] tabular-nums">{(ln.funding_date || "").slice(0, 10)}</td>
                  <td className="px-4 py-3"><span className="px-2 py-0.5 rounded-full bg-[#EFF6FF] text-[#1D4ED8] text-xs font-medium">{ln.current_status}</span></td>
                  <td className="px-4 py-3 text-right tabular-nums">{fmtMoney(ln.principal)}</td>
                  <td className="px-4 py-3 text-right tabular-nums text-[#0066FF] font-medium">{fmtMoney(ln.current_payoff)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export function DemoAdminDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    axios.get(`${API}/demo/admin/dashboard`).then((r) => setData(r.data)).finally(() => setLoading(false));
  }, []);
  if (loading) return <Splash label="Loading demo dashboard…" />;
  if (!data) return null;
  const k = data.kpis;
  return (
    <div className="min-h-screen bg-[#F7F9FC]" data-testid="demo-admin-dashboard">
      <DemoBanner />
      <div className="max-w-[1400px] mx-auto p-6 md:p-8">
        <div className="mb-6">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-[#6B7280] mb-1">
            <Sparkles className="w-3.5 h-3.5" /> Demo · Admin dashboard
          </div>
          <h1 className="font-display text-4xl font-semibold tracking-tight text-[#0A2540]">Instabridge Legal Funding</h1>
          <p className="text-sm text-[#6B7280] mt-1">Real-time portfolio operating system</p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <Card label="Active loans" value={k.active_loans} accent />
          <Card label="Outstanding principal" value={fmtMoney(k.total_outstanding_principal)} />
          <Card label="Current payoff exposure" value={fmtMoney(k.total_current_payoff)} hot />
          <Card label="Accrued interest" value={fmtMoney(k.total_accrued_interest)} />
          <Card label="Settled loans" value={k.settled_loans} />
          <Card label="Realized settlements" value={fmtMoney(k.total_settled_amount)} />
          <Card label="Total funded all-time" value={fmtMoney(k.total_funded_all_time)} />
          <Card label="Total loans" value={k.total_loans} />
        </div>
        <div className="bg-white border border-[#E5E7EB] rounded-lg overflow-hidden">
          <div className="px-5 py-3 border-b border-[#E5E7EB] flex items-center justify-between">
            <div className="font-medium text-[#0A2540] flex items-center gap-2"><FileSpreadsheet className="w-4 h-4 text-[#0066FF]" /> Recent loans</div>
            <span className="text-xs text-[#6B7280]">{data.loans.length} sample loans</span>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-[#F9FAFB]">
              <tr className="text-left text-[11px] uppercase tracking-wider text-[#6B7280]">
                <th className="px-4 py-2.5 font-medium">Client</th>
                <th className="px-4 py-2.5 font-medium">Rate</th>
                <th className="px-4 py-2.5 font-medium">Status</th>
                <th className="px-4 py-2.5 font-medium text-right">Principal</th>
                <th className="px-4 py-2.5 font-medium text-right">Payoff</th>
                <th className="px-4 py-2.5 font-medium text-right">Aging</th>
              </tr>
            </thead>
            <tbody>
              {data.loans.map((ln) => (
                <tr key={ln.loan_id} className="border-b border-[#F3F4F6]">
                  <td className="px-4 py-3 text-[#0A2540] font-medium">{ln.client_name}</td>
                  <td className="px-4 py-3 text-[#6B7280] tabular-nums">{(ln.monthly_rate * 100).toFixed(2)}%</td>
                  <td className="px-4 py-3"><span className="px-2 py-0.5 rounded-full bg-[#EFF6FF] text-[#1D4ED8] text-xs font-medium">{ln.current_status}</span></td>
                  <td className="px-4 py-3 text-right tabular-nums">{fmtMoney(ln.principal)}</td>
                  <td className="px-4 py-3 text-right tabular-nums text-[#0066FF] font-medium">{fmtMoney(ln.current_payoff)}</td>
                  <td className="px-4 py-3 text-right text-xs text-[#6B7280]">{ln.months_outstanding?.toFixed(1)} mo</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function DemoBanner() {
  return (
    <div className="bg-[#0A2540] text-white px-6 py-2.5 text-xs flex items-center justify-center gap-2" data-testid="demo-banner">
      <Sparkles className="w-3.5 h-3.5 text-[#FCD34D]" />
      <span>This is a public demo with fully fake data. No real client information is shown.</span>
    </div>
  );
}
function Card({ label, value, accent, hot }) {
  return (
    <div className="bg-white border border-[#E5E7EB] rounded-lg p-4">
      <div className="text-[11px] uppercase tracking-wider text-[#6B7280] mb-1.5">{label}</div>
      <div className={`tabular-nums font-display text-2xl font-semibold ${hot ? "text-[#B91C1C]" : accent ? "text-[#0066FF]" : "text-[#0A2540]"}`}>{value}</div>
    </div>
  );
}
function Splash({ label }) {
  return <div className="min-h-screen flex items-center justify-center text-[#6B7280] text-sm">{label}</div>;
}
